import { memo, useMemo } from 'react';
import { ConvertType, useFormatNumber } from '../../../hooks/useFormatNumber';
import { T } from '../../../translation/src';
import { AnnotationType } from '../../../modules/Properties/types';
import { PropertiesSection, Property } from './PropertiesSection';
import { PropertyPanel } from '../../../modules/Properties/components/PropertyPanel';
import { getOutlineOuterRingProperty, getOutlineCavitiesProperty, getOutlineFloorHeightProperty } from '@pointorama/potree-shared';
import type { Coordinate } from '@pointorama/potree-shared';
import { usePropertyValue } from '../../Properties/reactive/usePropertyValue';

interface OutlinePropertiesProps {
  identifier: string;
  projectId: string;
}

/** XY perimeter of a closed ring (wraps last → first). */
function ringPerimeter(ring: Coordinate[]): number {
  if (ring.length < 2) return 0;
  let total = 0;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i];
    const b = ring[(i + 1) % ring.length];
    total += Math.hypot(b.x - a.x, b.y - a.y);
  }
  return total;
}

const OutlineProperties_: React.FC2<OutlinePropertiesProps> = ({ identifier, projectId }) => {
  const { formatNumber } = useFormatNumber();

  // Outline geometry lives in reactive properties (mirrors how RoomProperties
  // reads the room polygon), so the panel updates live as the outline is edited.
  const outerRing = usePropertyValue(getOutlineOuterRingProperty(identifier));
  const cavities = usePropertyValue(getOutlineCavitiesProperty(identifier));
  const floorHeight = usePropertyValue(getOutlineFloorHeightProperty(identifier));

  const perimeter = useMemo(() => {
    let total = ringPerimeter(outerRing);
    for (const hole of cavities) total += ringPerimeter(hole);
    return total;
  }, [outerRing, cavities]);

  return (
    <div className="flex flex-col space-y-4 divide-y divide-gray-800">
      <PropertiesSection title={<T _str="information" swc />}>
        <Property title={<T _str="type" swc />}>
          <T _str="outer wall" swc />
        </Property>
        <Property title={<T _str="number of vertices" swc />}>{outerRing.length}</Property>
        <Property title={<T _str="number of cavities" swc />}>{cavities.length}</Property>
      </PropertiesSection>
      <PropertiesSection title={<T _str="measurements" swc />}>
        <Property title={<T _str="perimeter" swc />}>{formatNumber(perimeter, { convertType: ConvertType.DISTANCE, addUnit: true })}</Property>
      </PropertiesSection>
      <PropertiesSection title={<T _str="dimensions" swc />}>
        <Property title={<T _str="floor height" swc />}>{formatNumber(floorHeight, { convertType: ConvertType.DISTANCE, addUnit: true })}</Property>
      </PropertiesSection>
      <PropertyPanel projectId={projectId} annotationTypes={[AnnotationType.OUTLINE]} objectIdentifiers={[identifier]} />
    </div>
  );
};

export const OutlineProperties = memo(OutlineProperties_);
